import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  HarmonizedViolationAssigneeSource,
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
  HarmonizedViolationsQuery,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import { type AssigneeSource, assigneeInheritanceHint } from "./assigneeInheritance";
import {
  formatDaysToResolution,
  formatDiscoveryQuarter,
  formatReportedStatus,
  formatViolationDate,
  isViolationStale,
  PROGRAM_HEADER_HINT,
  REPORTED_STATUS_HEADER_HINT,
  ProgramChip,
  SEVERITY_HEADER_HINT,
  SeverityChip,
  GOV_STATUS_LABEL,
  STALE_HEADER_HINT,
  StaleChip,
  StatusChip,
  TrackedStatusChip,
} from "./violationDisplay";

export type ViolationRow = HarmonizedViolationsQuery["harmonizedViolations"]["items"][number];

type AssigneeUser = NonNullable<ViolationRow["assignee"]>;

/** Display an assignee as their person full name, falling back to email. */
export function assigneeDisplayName(user: AssigneeUser): string {
  return getFullName(user.person) || user.email;
}

// The cells below are components rather than inline `renderCell` bodies so they
// can be rendered — and asserted on — directly in tests, without faking the
// grid's params object.

/**
 * Hints which default an assignee was inherited from, so an inherited assignee
 * is distinguishable from an override without opening the drawer.
 */
export function AssigneeCell({
  user,
  source,
}: {
  user: AssigneeUser | null | undefined;
  source: AssigneeSource;
}) {
  if (!user) return <>—</>;
  const name = assigneeDisplayName(user);
  const hint = assigneeInheritanceHint(source);
  return (
    <Stack sx={{ minWidth: 0, py: 0.5 }}>
      <Typography variant="body2" noWrap title={name}>
        {name}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * The staff assignee has only a facility-level default, so its boolean maps onto
 * the FACILITY source and both assignee columns stay one code path.
 */
function staffAssigneeSource(isFacilityDefault: boolean): AssigneeSource {
  return isFacilityDefault ? HarmonizedViolationAssigneeSource.Facility : null;
}

/** Facility name, plus a chip when the violation spans co-located sites. */
export function FacilityCell({ name, siteCount }: { name: string; siteCount: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap title={name}>
        {name}
      </Typography>
      {siteCount > 1 ? (
        <Chip
          size="small"
          variant="outlined"
          label={`${siteCount} sites`}
          title="This violation applies to multiple co-located facilities. Open it to see all sites."
        />
      ) : null}
    </Box>
  );
}

/**
 * EPA's own resolution status, dated. The violationEndDate / lastSeenAt columns
 * are hidden by default, so this hover tooltip is where a user actually sees
 * when EPA resolved the violation.
 */
export function ReportedStatusCell({ row }: { row: ViolationRow }) {
  return (
    <StatusChip
      status={row.standardizedResolutionStatus}
      tooltip={formatReportedStatus(
        row.standardizedResolutionStatus,
        row.violationEndDate,
        row.lastSeenAt
      )}
    />
  );
}

/**
 * The user's own resolution status. Deliberately untooltipped: a date here
 * would read as EPA's, and this field is whatever the user last set.
 */
export function TrackedStatusCell({ status }: { status: ViolationRow["govStatus"] }) {
  return status ? <TrackedStatusChip status={status} /> : <>—</>;
}

/**
 * Columns depend on the latest refresh start (for the stale flag), so this is
 * a factory called inside a useMemo in the page rather than a module constant.
 *
 * IMPORTANT: program/severity/status are synthetic filter columns whose `field`
 * equals the Omnisearch filter key, NOT a Prisma column. severity and status are
 * sortable only because the resolver maps them back to real columns via
 * HARMONIZED_VIOLATION_SORT_KEY_MAP — a new synthetic column is NOT sortable
 * until it is added there, or the API will orderBy a non-existent column.
 *
 * Sorting an enum column sorts by Postgres declaration order, not alphabetically.
 * That is only worth exposing where declaration order means something (see the
 * per-column notes below); `program` is left unsortable for that reason.
 */
export function buildViolationColumns(
  refreshStartedAt: string | null | undefined,
  isStaff: boolean
): OmnisearchGridColDef<ViolationRow>[] {
  const staffColumns: OmnisearchGridColDef<ViolationRow>[] = isStaff
    ? [
        {
          field: "staffAssignee",
          headerName: "Staff assignee",
          description:
            "Encamp staff responsible party. Falls back to the facility default when not set on the violation. Visible to staff only.",
          flex: 0.35,
          sortable: false,
          renderCell: ({ row }) => (
            <AssigneeCell
              user={row.staffAssignee}
              source={staffAssigneeSource(row.staffAssigneeIsFacilityDefault)}
            />
          ),
        },
      ]
    : [];

  return [
    {
      field: "facility",
      headerName: "Facility",
      flex: 0.6,
      filterKeyType: "facility",
      valueGetter: (_value, row) => row.facility.name,
      renderCell: ({ row }) => <FacilityCell name={row.facility.name} siteCount={row.siteCount} />,
    },
    {
      // The violation is the most important thing in the row, so it sits
      // directly after the facility and carries the widest flex.
      field: "violationDescription",
      headerName: "Violation",
      flex: 0.8,
      renderCell: ({ row }) => (
        <Stack sx={{ minWidth: 0, py: 0.5 }}>
          <Typography variant="body2" noWrap title={row.violationDescription ?? ""}>
            {row.violationDescription ?? "—"}
          </Typography>
          {row.violationCode ? (
            <Typography variant="caption" color="text.secondary">
              {row.violationCode}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      // The detail drawer is titled with this ID, so showing it here ties the
      // drill-down back to the row it came from. The valueGetter type-checks
      // the row property so a rename can't silently blank the column.
      field: "violationId",
      headerName: "Violation ID",
      flex: 0.3,
      valueGetter: (_v: unknown, row: ViolationRow) => row.violationId,
    },
    {
      field: "assignee",
      headerName: "Assignee",
      description:
        "Responsible party. Falls back to the facility, region, or business unit default when not set on the violation.",
      flex: 0.35,
      sortable: false,
      renderCell: ({ row }) => <AssigneeCell user={row.assignee} source={row.assigneeSource} />,
    },
    ...staffColumns,
    {
      field: "program",
      headerName: "Program",
      description: PROGRAM_HEADER_HINT,
      flex: 0.42,
      // Not sortable: Postgres orders enums by declaration order, so ascending
      // program would be Water, Waste, Air — neither alphabetical nor meaningful.
      // Severity and status below are sortable precisely because their
      // declaration order IS the useful order. Filter by program instead.
      sortable: false,
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(HarmonizedViolationProgram),
      renderCell: ({ row }) => <ProgramChip program={row.sourceProgram} />,
    },
    {
      field: "severity",
      headerName: "Severity",
      description: SEVERITY_HEADER_HINT,
      flex: 0.3,
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(HarmonizedViolationSeverity),
      renderCell: ({ row }) => <SeverityChip severity={row.standardizedSeverity} />,
    },
    {
      field: "status",
      headerName: "Reported status",
      description: REPORTED_STATUS_HEADER_HINT,
      flex: 0.5,
      // Sortable, and ascending is deliberately not alphabetical: the enum is
      // declared UNRESOLVED, UNKNOWN, RESOLVED, NOT_REPORTED, so ascending walks
      // open work toward closed — which is the order a user wants.
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(HarmonizedViolationResolutionStatus),
      renderCell: ({ row }) => <ReportedStatusCell row={row} />,
    },
    {
      field: "violationStartDate",
      headerName: "Started",
      flex: 0.3,
      renderCell: ({ row }) => formatViolationDate(row.violationStartDate),
    },
    {
      field: "lastSeenAt",
      headerName: "Last seen",
      description: STALE_HEADER_HINT,
      flex: 0.35,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {formatViolationDate(row.lastSeenAt)}
          {isViolationStale(row.lastSeenAt, refreshStartedAt) ? <StaleChip /> : null}
        </Box>
      ),
    },
    {
      field: "violationAgency",
      headerName: "Agency",
      description: "Agency that recorded the violation.",
      flex: 0.3,
    },
    { field: "citations", headerName: "Regulatory citation", flex: 0.4 },
    { field: "pollutantTypeDesc", headerName: "Pollutant", flex: 0.3 },
    {
      field: "stdSeverityDeterminantDesc",
      headerName: "Severity reason",
      description:
        "Why this severity was assigned — e.g. air violation, RCRA significant non-compliance, or water Reportable Non-Compliance.",
      flex: 0.3,
    },
    { field: "epaFacilityId", headerName: "EPA facility ID", flex: 0.25 },
    { field: "registryId", headerName: "Registry ID", flex: 0.25 },
    { field: "state", headerName: "State", flex: 0.15 },
    {
      field: "violationEndDate",
      headerName: "Resolution date",
      flex: 0.25,
      renderCell: ({ row }) => formatViolationDate(row.violationEndDate),
    },
    {
      field: "permitTerminalDate",
      headerName: "Permit dead since",
      description: "Expiration/termination date of the permit that escalated this violation.",
      flex: 0.3,
      renderCell: ({ row }) => formatViolationDate(row.permitTerminalDate),
    },
    {
      field: "daysToResolution",
      headerName: "Days to resolution",
      description: "Calendar days from violation start to resolution date.",
      flex: 0.25,
      sortable: false,
      valueGetter: (_v: unknown, row: ViolationRow) =>
        formatDaysToResolution(row.violationStartDate, row.violationEndDate),
    },
    {
      // Read-only here; the resolution status is edited in the violation detail
      // drawer. Backed by the `govStatus` field (user-controlled, preserved
      // across data refreshes — distinct from EPA's derived status column).
      field: "govStatus",
      headerName: "Resolution status",
      description:
        "User-tracked resolution status, preserved across data refreshes. Editable in the violation's detail drawer.",
      // Widened for the long "Resolution In Progress (…)" labels, but at common
      // grid widths their distinguishing parenthetical still ellipsizes — the
      // chip's native title is what reliably carries the full label.
      flex: 0.55,
      valueGetter: (_v: unknown, row: ViolationRow) =>
        row.govStatus ? GOV_STATUS_LABEL[row.govStatus] : "",
      renderCell: ({ row }) => <TrackedStatusCell status={row.govStatus} />,
    },
    {
      // Not sortable: the value is a derived "YYYY Q#" string with no backing
      // Prisma column, and this grid sorts server-side.
      field: "quarterDiscovered",
      headerName: "Quarter discovered",
      description: "Quarter the violation started.",
      flex: 0.25,
      sortable: false,
      valueGetter: (_v: unknown, row: ViolationRow) =>
        formatDiscoveryQuarter(row.violationStartDate),
    },
  ];
}

// Focused default-visible set; every other column is available via manage-columns.
// `facility` is the leftmost column and stays visible.
export const VIOLATION_DEFAULT_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  facility: true,
  violationDescription: true,
  violationId: true,
  program: true,
  severity: true,
  status: true,
  assignee: true,
  // Staff-visible by default when present (the column is only built for staff).
  staffAssignee: true,
  govStatus: true,
  violationStartDate: true,
  // everything else hidden by default:
  lastSeenAt: false,
  violationAgency: false,
  citations: false,
  pollutantTypeDesc: false,
  stdSeverityDeterminantDesc: false,
  epaFacilityId: false,
  registryId: false,
  state: false,
  violationEndDate: false,
  daysToResolution: false,
  quarterDiscovered: false,
  permitTerminalDate: false,
};
